import { computed, DestroyRef, inject, Pipe, PipeTransform } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { EMPTY, Observable } from 'rxjs';
import { combineLatest, interval, map, startWith } from 'rxjs';
import { SettingsStore } from '@settings/data-access';
import { getDateTime } from '@shared/utils';

/**
 * Translation keys for relative date/time strings.
 */
const instantText = {
  year: 'COMPONENTS.RELATIVE_DATE.THIS_YEAR',
  quarter: 'COMPONENTS.RELATIVE_DATE.THIS_QUARTER',
  month: 'COMPONENTS.RELATIVE_DATE.THIS_MONTH',
  week: 'COMPONENTS.RELATIVE_DATE.THIS_WEEK',
  day: 'COMPONENTS.RELATIVE_DATE.TODAY',
  hour: 'COMPONENTS.RELATIVE_DATE.NOW',
  minute: 'COMPONENTS.RELATIVE_DATE.NOW',
  second: 'COMPONENTS.RELATIVE_DATE.NOW',
  millisecond: 'COMPONENTS.RELATIVE_DATE.NOW',
};

/**
 * Abstract base class providing shared logic for transforming dates into relative time strings.
 * @internal
 */
abstract class RelativeDateTimeBase {
  protected readonly translateService = inject(TranslateService);
  protected readonly destroyRef = inject(DestroyRef);
  private readonly settingsStore = inject(SettingsStore);
  private readonly culture = computed(() => this.settingsStore.ownerSettings()?.Culture);

  /**
   * Core logic for transforming a date into a relative time string.
   * @param value - The date to transform, either as an ISO string or Date object.
   * @param unit - The time unit to use for comparison (e.g., 'minute', 'hour'). Defaults to 'minute'.
   * @returns An Observable emitting a string representing the relative time (e.g., "5 minutes ago" or "now").
   * @protected
   */
  protected transformLogic(value: string, unit: keyof typeof instantText = 'minute'): Observable<string> {
    let pastDate: DateTime | null;
    if (typeof value === 'string') {
      // O valor chega já na hora de parede da conta: a conversão de UTC acontece
      // na fronteira com o backend (`DateInterceptor`, tanto para os campos
      // `*Utc` como para as exceções declaradas em `withUtcDateFields`).
      // Reconverter aqui deslocaria o valor pelo offset da conta — era o que 13
      // templates faziam ao passar `isConvertedUtc: false` sobre campos já
      // convertidos.
      pastDate = getDateTime(value);
    } else {
      return EMPTY;
    }

    const translations$ = this.translateService.stream([
      'COMPONENTS.RELATIVE_DATE.INVALID_DATE',
      instantText[unit]
    ]).pipe(takeUntilDestroyed(this.destroyRef));

    const computeRelative = (translations: Record<string, string>): string => {
      if (!pastDate?.isValid) {
        return translations['COMPONENTS.RELATIVE_DATE.INVALID_DATE'];
      }

      const basePast = pastDate.endOf(unit);
      const now = DateTime.now().endOf(unit);
      const diffNow = basePast.diff(now).as(unit);
      const isNow = diffNow >= 0 && diffNow < 1;
      if (isNow) {
        return translations[instantText[unit]];
      }

      return basePast.toRelative({ locale: this.culture(), base: now }) ?? '';
    };

    if (unit === 'second') {
      return combineLatest([
        translations$,
        interval(1000).pipe(startWith(0))
      ]).pipe(
        map(([translations]) => computeRelative(translations)),
        takeUntilDestroyed(this.destroyRef)
      );
    }

    return translations$.pipe(map(computeRelative));
  }
}

/**
 * A pure Angular pipe that transforms a date into a relative time string (e.g., "5 minutes ago").
 * @public
 */
@Pipe({
  name: 'relativeDateTime',
  pure: true,
  standalone: true
})
export class RelativeDateTimePipe extends RelativeDateTimeBase implements PipeTransform {
  /**
   * Transforms a date string or Date object into a relative time string.
   * @param value - The date to transform, either as an ISO string or Date object.
   * @param unit - The time unit to use for comparison (e.g., 'minute', 'hour'). Defaults to 'minute'.
   * @returns An Observable emitting a string representing the relative time (e.g., "5 minutes ago" or "now").
   */
  transform(value: string | Date | null | undefined, unit: keyof typeof instantText = 'minute'): Observable<string> {
    if (value == null) return EMPTY;
    const isoString = getDateTime(value)?.toISO();
    if (!isoString) return EMPTY;
    return this.transformLogic(isoString, unit);
  }
}

/**
 * An impure Angular pipe that transforms a date into a relative time string, updating reactively (e.g., "5 minutes ago").
 * @public
 */
@Pipe({
  name: 'relativeDateTimeImpure',
  pure: false,
  standalone: true
})
export class RelativeDateTimeImpurePipe extends RelativeDateTimeBase implements PipeTransform {
  /**
   * Transforms a date string or Date object into a relative time string, updating reactively.
   * @param value - The date to transform, either as an ISO string or Date object.
   * @param unit - The time unit to use for comparison (e.g., 'minute', 'hour'). Defaults to 'minute'.
   * @returns An Observable emitting a string representing the relative time (e.g., "5 minutes ago" or "now").
   */
  transform(value: string | Date | null | undefined, unit: keyof typeof instantText = 'minute'): Observable<string> {
    if (value == null) return EMPTY;
    const isoString = getDateTime(value)?.toISO();
    if (!isoString) return EMPTY;
    return this.transformLogic(isoString, unit);
  }
}